import { useQuery } from '@tanstack/react-query';

import { api } from '@/shared/api/http';

/*
 * Version of the running SPA and the API (README "Версионирование и релизы"). The SPA's values are compiled in by Vite
 * (`vite.config.ts` → `__APP_*__`): package.json's version (bumped by release-please), the git commit of the build and
 * the build time. The API answers `GET /api/version` anonymously.
 */

/** Release version of the SPA, e.g. `1.0.0`. */
export const APP_VERSION: string = __APP_VERSION__;

/** Full git commit the SPA was built from; null when unknown (a build without git). */
export const APP_COMMIT: string | null = __APP_COMMIT__ || null;

/** ISO time of the SPA build. */
export const APP_BUILT_AT: string | null = __APP_BUILT_AT__ || null;

/** The project's repository: release notes, changelog and commits. */
export const REPOSITORY_URL = 'https://github.com/tksttz5kp8-dotcom/orbita';

/** `git rev-parse --short`-style abbreviation (7 characters). */
export function shortCommit(commit: string): string {
  return commit.slice(0, 7);
}

export function commitUrl(commit: string): string {
  return `${REPOSITORY_URL}/commit/${encodeURIComponent(commit)}`;
}

/** The GitHub release of a version (tags are `v<version>`). */
export function releaseUrl(version: string): string {
  return `${REPOSITORY_URL}/releases/tag/v${encodeURIComponent(version)}`;
}

export const CHANGELOG_URL = `${REPOSITORY_URL}/blob/main/CHANGELOG.md`;

/** `GET /api/version`: the build of the API. */
export interface ServerVersion {
  version: string;
  commit: string | null;
  builtAt: string | null;
}

export const SERVER_VERSION_QUERY_KEY = ['app', 'version'] as const;

export const fetchServerVersion = (signal?: AbortSignal) =>
  api.get<ServerVersion>('/version', { auth: false, signal });

/** The API's build (cached for five minutes; the server allows a minute plus ETag revalidation). */
export function useServerVersion(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: SERVER_VERSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchServerVersion(signal),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: options.enabled ?? true,
  });
}

/** Two builds differ when their versions or (both known) commits differ. */
export function isSameBuild(
  a: { version: string; commit: string | null },
  b: { version: string; commit: string | null },
): boolean {
  if (a.version !== b.version) return false;
  return !a.commit || !b.commit || a.commit === b.commit;
}
